"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "../context/AuthContext";
import { apiRequest } from "../utils/api";
import { formatCurrency } from "../utils/currency";
import { handleMediaError, resolveMediaUrl } from "../utils/media";
import { 
  User as UserIcon, ClipboardList, Laptop, ShoppingCart, Users, 
  Plus, Edit2, Trash2, Key, Save, ShieldAlert, Check, X, UploadCloud, Star
} from "lucide-react";
import toast from "react-hot-toast";
import PhoneNumberInput from "../components/PhoneNumberInput";
import { INVALID_PHONE_MESSAGE, isValidBangladeshiPhoneNumber } from "../utils/phone";

export default function DashboardPage() {
  const { user, isAuthenticated, isLoading: authLoading, updateProfile, changePassword } = useAuth();
  const router = useRouter();

  const [activeTab, setActiveTab] = useState("profile");

  // Shared state: Orders (User & Admin)
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Admin state: Products
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);

  // Product Form fields
  const [prodName, setProdName] = useState("");
  const [prodDesc, setProdDesc] = useState("");
  const [prodPrice, setProdPrice] = useState("");
  const [prodStock, setProdStock] = useState("");
  const [prodCategory, setProdCategory] = useState("");
  const [prodFeatured, setProdFeatured] = useState(false);
  const [prodImages, setProdImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Admin state: Users List
  const [usersList, setUsersList] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // User profile editor state
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileAddress, setProfileAddress] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);

  // User Change Password state
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Direct checkout auth guard
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error("You must log in to view the dashboard.");
      router.push("/login");
    }
  }, [isAuthenticated, authLoading, router]);

  // Load profile values on mount
  useEffect(() => {
    if (user) {
      setProfileName(user.name || "");
      setProfilePhone(user.phoneNumber || "");
      setProfileAddress(user.deliveryAddress || "");
      setProfileImage(user.profileImage || "");
    }
  }, [user]);

  // Fetch data depending on active tab
  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const endpoint = user?.role === "USER" ? "/orders/my-orders" : "/orders";
      const response = await apiRequest(endpoint);
      if (response?.success && response.data) {
        // Handle paginated structure for admin
        const list = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setOrders(list);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load orders.");
    } finally {
      setOrdersLoading(false);
    }
  }, [user?.role]);

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const response = await apiRequest("/products?limit=50");
      if (response?.success && response.data) {
        setProducts(response.data);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load products inventory.");
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await apiRequest("/users");
      if (response?.success && response.data) {
        const list = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setUsersList(list);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load users list.");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Categories dynamic state
  const [categories, setCategories] = useState<any[]>([]);

  // Quick Category states
  const [isAddingNewCat, setIsAddingNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const handleQuickAddCategory = async () => {
    if (!newCatName.trim()) {
      toast.error("Category name cannot be empty.");
      return;
    }
    try {
      const response = await apiRequest("/categories", {
        method: "POST",
        body: JSON.stringify({ name: newCatName.trim() }),
      });
      if (response?.success && response.data) {
        toast.success("Category created successfully!");
        setNewCatName("");
        setIsAddingNewCat(false);
        // Refresh categories list
        const updatedResponse = await apiRequest("/categories");
        if (updatedResponse?.success && updatedResponse.data) {
          setCategories(updatedResponse.data);
        }
        // Select the newly created category
        setProdCategory(response.data.name);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to create category.");
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiRequest("/categories");
      if (response?.success && response.data) {
        setCategories(response.data);
        if (response.data.length > 0 && !prodCategory) {
          setProdCategory(response.data[0].name);
        }
      }
    } catch {
      toast.error("Failed to load categories list.");
    }
  }, [prodCategory]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === "orders") {
      fetchOrders();
    } else if (activeTab === "products" && user.role !== "USER") {
      fetchProducts();
      fetchCategories();
    } else if (activeTab === "users" && user.role !== "USER") {
      fetchUsers();
    }
  }, [activeTab, user, fetchOrders, fetchProducts, fetchUsers, fetchCategories]);

  // --- Normal User Actions ---
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (profilePhone && !isValidBangladeshiPhoneNumber(profilePhone)) {
      toast.error(INVALID_PHONE_MESSAGE);
      return;
    }
    setUpdatingProfile(true);
    try {
      const response = await updateProfile({
        name: profileName,
        phoneNumber: profilePhone || undefined,
        deliveryAddress: profileAddress,
        profileImage: profileImage || undefined,
      });
      if (response?.success) {
        toast.success("Profile updated successfully!");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update profile.");
    } finally {
      setUpdatingProfile(false);
    }
  };

  const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }

    setUploadingProfileImage(true);
    const formData = new FormData();
    formData.append("images", file);

    try {
      const response = await apiRequest("/upload/multiple/images", {
        method: "POST",
        body: formData,
      });

      if (response?.success && Array.isArray(response.data) && response.data[0]) {
        setProfileImage(response.data[0]);
        toast.success("Profile picture uploaded. Save profile to apply it.");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Profile picture upload failed.";
      toast.error(message);
    } finally {
      setUploadingProfileImage(false);
      e.target.value = "";
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    setUpdatingPassword(true);
    try {
      const response = await changePassword({ oldPassword, newPassword });
      if (response?.success) {
        toast.success("Password changed successfully!");
        setOldPassword("");
        setNewPassword("");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to change password. Verify your current password.");
    } finally {
      setUpdatingPassword(false);
    }
  };

  // --- Admin Product CRUD ---
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("images", files[i]);
    }

    try {
      const response = await apiRequest("/upload/multiple/images", {
        method: "POST",
        body: formData,
      });
      if (response?.success && Array.isArray(response.data)) {
        setProdImages((prev) => [...prev, ...response.data]);
        toast.success("Images uploaded successfully!");
      }
    } catch (err: any) {
      toast.error(err.message || "Image upload failed.");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prodName || !prodPrice || !prodStock || prodImages.length === 0) {
      toast.error("Please fill in required fields and upload at least one image.");
      return;
    }

    const payload = {
      name: prodName,
      description: prodDesc,
      price: parseFloat(prodPrice),
      stock: parseInt(prodStock),
      category: prodCategory,
      isFeatured: prodFeatured,
      images: prodImages,
    };

    try {
      let response;
      if (editingProduct) {
        response = await apiRequest(`/products/${editingProduct.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        response = await apiRequest("/products", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      if (response?.success) {
        toast.success(`Product ${editingProduct ? "updated" : "created"} successfully!`);
        setIsProductModalOpen(false);
        setEditingProduct(null);
        clearProductForm();
        fetchProducts();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save product.");
    }
  };

  const handleEditProductClick = (product: any) => {
    setEditingProduct(product);
    setProdName(product.name);
    setProdDesc(product.description);
    setProdPrice(product.price.toString());
    setProdStock(product.stock.toString());
    setProdCategory(product.category);
    setProdFeatured(product.isFeatured);
    setProdImages(product.images);
    setIsProductModalOpen(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      const response = await apiRequest(`/products/${productId}`, {
        method: "DELETE",
      });
      if (response?.success) {
        toast.success("Product deleted from database.");
        fetchProducts();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete product.");
    }
  };

  const clearProductForm = () => {
    setProdName("");
    setProdDesc("");
    setProdPrice("");
    setProdStock("");
    setProdCategory(categories[0]?.name || "");
    setProdFeatured(false);
    setProdImages([]);
    setIsAddingNewCat(false);
    setNewCatName("");
  };

  // --- Admin Order Actions ---
  const handleUpdateOrderStatus = async (orderId: string, orderStatus: string, paymentStatus: string) => {
    try {
      const response = await apiRequest(`/orders/${orderId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ orderStatus, paymentStatus }),
      });
      if (response?.success) {
        toast.success("Order status updated successfully!");
        fetchOrders();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update order status.");
    }
  };

  // --- Admin User Actions ---
  const handleUpdateUserStatus = async (targetUser: any, updates: { role?: string; status?: string }) => {
    try {
      const payload = {
        role: updates.role || targetUser.role,
        status: updates.status || targetUser.status,
      };
      const response = await apiRequest(`/users/${targetUser.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (response?.success) {
        toast.success("User configuration updated successfully!");
        fetchUsers();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update user config.");
    }
  };

  if (authLoading || !user) {
    return (
      <div className="flex-1 flex justify-center items-center py-20 bg-zinc-50 dark:bg-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
      </div>
    );
  }

  // Dashboard Tab Config based on Role
  const isUser = user.role === "USER";
  const tabsList = [
    { id: "profile", label: "My Profile", icon: UserIcon },
    { id: "orders", label: isUser ? "My Orders" : "Manage Orders", icon: ClipboardList },
    ...(!isUser ? [
      { id: "products", label: "Manage Products", icon: Laptop },
      { id: "users", label: "Manage Users", icon: Users },
    ] : []),
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 w-full flex-1 bg-zinc-50 dark:bg-black transition-colors duration-300">
      
      {/* Header Info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-200 dark:border-zinc-900 pb-6 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 dark:text-white tracking-tight">
            Dashboard Panel
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Welcome back, <span className="font-semibold text-zinc-700 dark:text-zinc-300">{user.name}</span> &middot; Role:{" "}
            <span className="font-bold uppercase text-violet-600 dark:text-violet-400 text-xs bg-violet-600/5 px-2 py-0.5 rounded border border-violet-500/15">
              {user.role}
            </span>
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8 items-start">
        
        {/* Sidebar Nav */}
        <aside className="w-full md:w-64 shrink-0 flex flex-row md:flex-col gap-1.5 overflow-x-auto md:overflow-x-visible border-b md:border-b-0 border-zinc-200 dark:border-zinc-900 pb-4 md:pb-0">
          {tabsList.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all w-fit md:w-full shrink-0 ${
                  activeTab === tab.id
                    ? "bg-violet-600 text-white shadow-md shadow-violet-600/15"
                    : "text-zinc-650 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-900"
                }`}
              >
                <Icon className="h-4.5 w-4.5" />
                {tab.label}
              </button>
            );
          })}
        </aside>

        {/* Dynamic Tab Panel Area */}
        <main className="flex-1 w-full bg-white dark:bg-zinc-950 p-6 md:p-8 rounded-2xl border border-zinc-200 dark:border-zinc-900 shadow-sm min-h-[400px]">
          
          {/* 1. Profile Panel */}
          {activeTab === "profile" && (
            <div className="grid md:grid-cols-12 gap-8">
              
              {/* Profile Details Edit Form */}
              <form onSubmit={handleUpdateProfile} className="md:col-span-7 space-y-6">
                <h3 className="text-base font-bold text-zinc-900 dark:text-white border-b border-zinc-100 dark:border-zinc-900 pb-2">
                  Update Account Details
                </h3>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-505 dark:text-zinc-400 font-semibold block">Full Name</label>
                    <input
                      type="text"
                      required
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-550 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="profile-phone" className="text-xs text-zinc-505 dark:text-zinc-400 font-semibold block">Phone Number</label>
                    <PhoneNumberInput id="profile-phone" value={profilePhone} onChange={setProfilePhone} />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs text-zinc-505 dark:text-zinc-400 font-semibold block">Profile Picture</label>
                    <div className="flex flex-wrap items-center gap-4">
                      {profileImage ? (
                        <img
                          src={resolveMediaUrl(profileImage)}
                          alt="Profile preview"
                          onError={handleMediaError}
                          className="h-16 w-16 rounded-full border border-zinc-200 dark:border-zinc-850 object-cover"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full border border-zinc-200 dark:border-zinc-850 bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center text-lg font-bold text-zinc-500">
                          {profileName ? profileName[0].toUpperCase() : "U"}
                        </div>
                      )}
                      <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-850 px-4 py-2 text-xs font-semibold text-zinc-650 dark:text-zinc-350 hover:bg-zinc-50 dark:hover:bg-zinc-900 cursor-pointer">
                        <UploadCloud className="h-4.5 w-4.5 text-zinc-400" />
                        {uploadingProfileImage ? "Uploading..." : "Choose Image"}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProfileImageUpload}
                          disabled={uploadingProfileImage}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-505 dark:text-zinc-400 font-semibold block">Delivery Address</label>
                    <textarea
                      rows={4}
                      value={profileAddress}
                      onChange={(e) => setProfileAddress(e.target.value)}
                      placeholder="House number, street address, city, postcode..."
                      className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-550 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={updatingProfile || uploadingProfileImage || Boolean(profilePhone && !isValidBangladeshiPhoneNumber(profilePhone))}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 text-white px-5 py-2 text-sm font-semibold hover:bg-violet-500 transition-colors disabled:opacity-50"
                >
                  <Save className="h-4.5 w-4.5" />
                  {updatingProfile ? "Saving Details..." : "Save Profile"}
                </button>
              </form>

              {/* Password change Panel */}
              <form onSubmit={handleChangePassword} className="md:col-span-5 space-y-6 border-t md:border-t-0 md:border-l border-zinc-100 dark:border-zinc-900 pt-6 md:pt-0 md:pl-8">
                <h3 className="text-base font-bold text-zinc-900 dark:text-white border-b border-zinc-100 dark:border-zinc-900 pb-2">
                  Change Password
                </h3>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-505 dark:text-zinc-400 font-semibold block">Current Password</label>
                    <input
                      type="password"
                      required
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-550"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-zinc-505 dark:text-zinc-400 font-semibold block">New Password</label>
                    <input
                      type="password"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-550"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={updatingPassword}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-950 dark:bg-zinc-100 text-white dark:text-zinc-950 px-5 py-2 text-sm font-semibold hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
                >
                  <Key className="h-4.5 w-4.5" />
                  {updatingPassword ? "Updating..." : "Update Password"}
                </button>
              </form>
            </div>
          )}

          {/* 2. Manage/View Orders Panel */}
          {activeTab === "orders" && (
            <div className="space-y-6">
              <h3 className="text-base font-bold text-zinc-900 dark:text-white border-b border-zinc-100 dark:border-zinc-900 pb-2">
                {isUser ? "Purchase Transactions" : "System Invoices"}
              </h3>

              {ordersLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
                </div>
              ) : orders.length > 0 ? (
                <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-900 rounded-xl">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-900 text-zinc-400 text-xs uppercase font-bold">
                        <th className="p-4">Reference Reference</th>
                        <th className="p-4">Date</th>
                        {!isUser && <th className="p-4">Customer</th>}
                        <th className="p-4">Amount</th>
                        <th className="p-4">Order Status</th>
                        <th className="p-4">Payment</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200 dark:divide-zinc-900">
                      {orders.map((ord) => (
                        <tr key={ord.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-950/50">
                          <td className="p-4 font-mono font-semibold text-xs text-zinc-900 dark:text-white">
                            {ord.id.substring(0, 10)}...
                          </td>
                          <td className="p-4 text-xs text-zinc-550">
                            {new Date(ord.createdAt).toLocaleDateString()}
                          </td>
                          {!isUser && (
                            <td className="p-4">
                              <span className="font-semibold text-zinc-900 dark:text-white block">{ord.customerName}</span>
                              <span className="text-[10px] text-zinc-400 block">{ord.customerPhone}</span>
                            </td>
                          )}
                          <td className="p-4 font-bold text-zinc-900 dark:text-white">
                            {formatCurrency(ord.totalAmount)}
                          </td>
                          <td className="p-4">
                            {isUser ? (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                ord.orderStatus === "PENDING" ? "bg-amber-500/10 text-amber-500 border border-amber-500/20" :
                                ord.orderStatus === "DELIVERED" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" :
                                ord.orderStatus === "CANCELLED" ? "bg-rose-500/10 text-rose-500 border border-rose-500/20" :
                                "bg-indigo-500/10 text-indigo-500 border border-indigo-500/20"
                              }`}>
                                {ord.orderStatus}
                              </span>
                            ) : (
                              <select
                                value={ord.orderStatus}
                                onChange={(e) => handleUpdateOrderStatus(ord.id, e.target.value, ord.paymentStatus)}
                                className="text-xs bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded px-2 py-1"
                              >
                                <option value="PENDING">PENDING</option>
                                <option value="PROCESSING">PROCESSING</option>
                                <option value="SHIPPED">SHIPPED</option>
                                <option value="DELIVERED">DELIVERED</option>
                                <option value="CANCELLED">CANCELLED</option>
                              </select>
                            )}
                          </td>
                          <td className="p-4">
                            {isUser ? (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                ord.paymentStatus === "PAID" ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-550"
                              }`}>
                                {ord.paymentStatus}
                              </span>
                            ) : (
                              <select
                                value={ord.paymentStatus}
                                onChange={(e) => handleUpdateOrderStatus(ord.id, ord.orderStatus, e.target.value)}
                                className="text-xs bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded px-2 py-1"
                              >
                                <option value="PENDING">PENDING</option>
                                <option value="PAID">PAID</option>
                                <option value="FAILED">FAILED</option>
                              </select>
                            )}
                          </td>
                          <td className="p-4 text-right space-x-2 shrink-0">
                            <button
                              onClick={() => router.push(`/orders/${ord.id}`)}
                              className="text-xs font-semibold text-violet-600 dark:text-violet-400 hover:underline"
                            >
                              Invoice
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 border border-dashed border-zinc-200 dark:border-zinc-900 rounded-xl">
                  <p className="text-zinc-550 text-sm">No historical transaction reference.</p>
                </div>
              )}
            </div>
          )}

          {/* 3. Manage Products Panel (Admin/Super Admin only) */}
          {activeTab === "products" && user.role !== "USER" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-900 pb-2">
                <h3 className="text-base font-bold text-zinc-900 dark:text-white">
                  Device Catalog ({products.length})
                </h3>
                <button
                  onClick={() => {
                    setEditingProduct(null);
                    clearProductForm();
                    setIsProductModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1 rounded-full bg-violet-600 text-white px-4 py-1.5 text-xs font-bold hover:bg-violet-500 shadow-md cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  Add Product
                </button>
              </div>

              {productsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
                </div>
              ) : products.length > 0 ? (
                <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-900 rounded-xl">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-900 text-zinc-400 text-xs uppercase font-bold">
                        <th className="p-4">Details</th>
                        <th className="p-4">Category</th>
                        <th className="p-4">Pricing</th>
                        <th className="p-4">Stock</th>
                        <th className="p-4">Featured</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200 dark:divide-zinc-900">
                      {products.map((prod) => (
                        <tr key={prod.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-950/50">
                          <td className="p-4 flex items-center gap-3">
                            <div className="h-10 w-10 shrink-0 bg-zinc-50 dark:bg-zinc-900 border border-zinc-250 dark:border-zinc-800 rounded flex items-center justify-center p-1">
                              <img src={resolveMediaUrl(prod.images?.[0])} alt="" onError={handleMediaError} className="max-h-full max-w-full object-contain" />
                            </div>
                            <span className="font-semibold text-zinc-900 dark:text-white truncate max-w-[180px] block" title={prod.name}>
                              {prod.name}
                            </span>
                          </td>
                          <td className="p-4 text-xs">
                            <span className="font-semibold text-zinc-650 dark:text-zinc-350 block uppercase">{prod.category}</span>
                          </td>
                          <td className="p-4 font-bold text-zinc-900 dark:text-white">
                            {formatCurrency(prod.price)}
                          </td>
                          <td className="p-4 font-semibold text-zinc-700 dark:text-zinc-300">
                            {prod.stock} Units
                          </td>
                          <td className="p-4 text-xs font-semibold text-indigo-400">
                            {prod.isFeatured ? "Yes" : "No"}
                          </td>
                          <td className="p-4 text-right space-x-2 shrink-0">
                            <button
                              onClick={() => handleEditProductClick(prod)}
                              className="p-1 text-zinc-400 hover:text-indigo-500 rounded hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                              title="Edit product"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(prod.id)}
                              className="p-1 text-zinc-400 hover:text-rose-500 rounded hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                              title="Delete product"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 border border-dashed border-zinc-200 dark:border-zinc-900 rounded-xl">
                  <p className="text-zinc-550 text-sm">Products list is empty. Click the button to add devices.</p>
                </div>
              )}
            </div>
          )}

          {/* 4. Manage Users Panel (Admin/Super Admin only) */}
          {activeTab === "users" && user.role !== "USER" && (
            <div className="space-y-6">
              <h3 className="text-base font-bold text-zinc-900 dark:text-white border-b border-zinc-100 dark:border-zinc-900 pb-2">
                Users Account Panel ({usersList.length})
              </h3>

              {usersLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
                </div>
              ) : usersList.length > 0 ? (
                <div className="overflow-x-auto border border-zinc-200 dark:border-zinc-900 rounded-xl">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-900 text-zinc-400 text-xs uppercase font-bold">
                        <th className="p-4">Customer Details</th>
                        <th className="p-4">Registered Email</th>
                        <th className="p-4">Access Level</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200 dark:divide-zinc-900">
                      {usersList.map((target) => (
                        <tr key={target.id} className="hover:bg-zinc-50/50 dark:hover:bg-zinc-950/50">
                          <td className="p-4 flex items-center gap-3">
                            {target.profileImage ? (
                              <img src={resolveMediaUrl(target.profileImage)} alt="" onError={handleMediaError} className="h-8 w-8 rounded-full object-cover" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center text-xs font-bold">
                                {target.name[0].toUpperCase()}
                              </div>
                            )}
                            <span className="font-semibold text-zinc-900 dark:text-white block">{target.name}</span>
                          </td>
                          <td className="p-4 text-xs text-zinc-550">
                            {target.email}
                          </td>
                          <td className="p-4">
                            <span className="font-bold text-xs bg-zinc-50 dark:bg-zinc-900 px-2 py-0.5 rounded border border-zinc-200 dark:border-zinc-850 text-zinc-650 dark:text-zinc-350">
                              {target.role}
                            </span>
                          </td>
                          <td className="p-4 text-xs">
                            <span className={`px-2 py-0.5 rounded-full font-bold ${
                              target.status === "ACTIVE" ? "bg-emerald-500/10 text-emerald-500" :
                              target.status === "BLOCKED" ? "bg-rose-500/10 text-rose-500" : "bg-amber-500/10 text-amber-550"
                            }`}>
                              {target.status}
                            </span>
                          </td>
                          <td className="p-4 text-right space-x-2 shrink-0">
                            {user.role === "SUPER_ADMIN" && (
                              <button
                                onClick={() => handleUpdateUserStatus(target, { role: target.role === "ADMIN" ? "USER" : "ADMIN" })}
                                className="text-xs font-semibold text-indigo-500 hover:underline"
                              >
                                Toggle Admin
                              </button>
                            )}
                            {target.id !== user.id && (
                              <button
                                onClick={() => handleUpdateUserStatus(target, { status: target.status === "BLOCKED" ? "ACTIVE" : "BLOCKED" })}
                                className={`text-xs font-semibold hover:underline ${
                                  target.status === "BLOCKED" ? "text-emerald-500" : "text-rose-500"
                                }`}
                              >
                                {target.status === "BLOCKED" ? "Activate" : "Block"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 border border-dashed border-zinc-200 dark:border-zinc-900 rounded-xl">
                  <p className="text-zinc-555 text-sm">Users listing is empty.</p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Admin Product Form Overlay Modal */}
      {isProductModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white dark:bg-zinc-950 max-w-2xl w-full rounded-2xl border border-zinc-200 dark:border-zinc-900 shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-900 bg-zinc-50 dark:bg-zinc-900/40">
              <h3 className="text-base font-bold text-zinc-900 dark:text-white">
                {editingProduct ? "Edit Product Specifications" : "Create New Device Listing"}
              </h3>
              <button
                onClick={() => {
                  setIsProductModalOpen(false);
                  setEditingProduct(null);
                  clearProductForm();
                }}
                className="p-1 rounded-lg text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveProduct} className="flex-1 overflow-y-auto p-6 space-y-6">
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Title */}
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs text-zinc-500 font-semibold block">Device Title *</label>
                  <input
                    type="text"
                    required
                    value={prodName}
                    onChange={(e) => setProdName(e.target.value)}
                    placeholder="e.g. iPhone 15 Pro Max"
                    className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Category Selection */}
                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs text-zinc-500 font-semibold block">Device Category *</label>
                    <button
                      type="button"
                      onClick={() => setIsAddingNewCat(!isAddingNewCat)}
                      className="text-[10px] text-violet-600 dark:text-violet-400 font-bold hover:underline"
                    >
                      {isAddingNewCat ? "Select Existing" : "+ Create New Category"}
                    </button>
                  </div>
                  {isAddingNewCat ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCatName}
                        onChange={(e) => setNewCatName(e.target.value)}
                        placeholder="New category name..."
                        className="flex-1 px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={handleQuickAddCategory}
                        className="px-3 py-2 text-xs font-bold bg-violet-600 hover:bg-violet-500 rounded text-white"
                      >
                        Add
                      </button>
                    </div>
                  ) : (
                    <select
                      value={prodCategory}
                      onChange={(e) => setProdCategory(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Price */}
                <div className="space-y-1.5">
                  <label className="text-xs text-zinc-500 font-semibold block">Price (BDT/Tk) *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={prodPrice}
                    onChange={(e) => setProdPrice(e.target.value)}
                    placeholder="e.g. 1099"
                    className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Stock */}
                <div className="space-y-1.5">
                  <label className="text-xs text-zinc-500 font-semibold block">Stock Units *</label>
                  <input
                    type="number"
                    required
                    value={prodStock}
                    onChange={(e) => setProdStock(e.target.value)}
                    placeholder="e.g. 25"
                    className="w-full px-3 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Featured Checkbox */}
                <div className="sm:col-span-2 py-2">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      checked={prodFeatured}
                      onChange={(e) => setProdFeatured(e.target.checked)}
                      className="h-4.5 w-4.5 rounded accent-violet-600 border-zinc-300"
                    />
                    Highlight Product (Mark as Featured item)
                  </label>
                </div>

                {/* Detailed Description */}
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs text-zinc-500 font-semibold block">Detailed Specifications *</label>
                  <textarea
                    required
                    rows={4}
                    value={prodDesc}
                    onChange={(e) => setProdDesc(e.target.value)}
                    placeholder="Enter screen specs, hardware chip, RAM configuration, battery level..."
                    className="w-full px-3.5 py-2 text-sm rounded bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Multiple Image Upload box */}
                <div className="space-y-2 sm:col-span-2">
                  <label className="text-xs text-zinc-500 font-semibold block">Upload Product Images *</label>
                  
                  <div className="flex gap-4 items-center">
                    <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-zinc-300 dark:border-zinc-850 rounded-lg cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900 text-xs font-semibold text-zinc-650 dark:text-zinc-350">
                      <UploadCloud className="h-4.5 w-4.5 text-zinc-400" />
                      Choose Local Images
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploadingImages}
                      />
                    </label>
                    {uploadingImages && (
                      <span className="text-xs text-zinc-450 animate-pulse font-semibold">
                        Uploading to cloud space...
                      </span>
                    )}
                  </div>

                  {/* Thumbnail Previews */}
                  {prodImages.length > 0 && (
                    <div className="flex flex-wrap gap-2.5 pt-2">
                      {prodImages.map((img, idx) => (
                        <div key={idx} className="relative h-14 w-14 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-850 p-1 overflow-hidden flex items-center justify-center group">
                          <img src={resolveMediaUrl(img)} alt="" onError={handleMediaError} className="max-h-full max-w-full object-contain" />
                          <button
                            type="button"
                            onClick={() => setProdImages(prodImages.filter((_, i) => i !== idx))}
                            className="absolute -top-1 -right-1 h-5 w-5 bg-rose-600 rounded-full flex items-center justify-center text-white scale-0 group-hover:scale-100 transition-all font-black text-[9px]"
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 border-t border-zinc-100 dark:border-zinc-900 pt-4 mt-6">
                <button
                  type="submit"
                  disabled={uploadingImages}
                  className="flex-1 inline-flex items-center justify-center rounded-lg bg-violet-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-violet-500 transition-colors disabled:opacity-50"
                >
                  {editingProduct ? "Update Specifications" : "Create Listing"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsProductModalOpen(false);
                    setEditingProduct(null);
                    clearProductForm();
                  }}
                  className="rounded-lg border border-zinc-200 dark:border-zinc-850 px-4 py-2.5 text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900"
                >
                  Cancel
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}
